import { useCallback, useRef, useState } from 'react';
import { aiApi } from '../api/ai.js';

// Map a short language code to a BCP-47 tag for the Web Speech API.
function toBcp47(language) {
  if (!language) return 'it-IT';
  if (language.includes('-')) return language;
  const map = { it: 'it-IT', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', pt: 'pt-PT' };
  return map[language] || language;
}

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

/**
 * Push-to-talk dictation.
 *
 * Live path: the browser Web Speech API streams interim results so the text
 * appears while speaking. `start(onUpdate)` calls onUpdate(fullText) on every
 * interim/final result.
 *
 * Fallback path (no Web Speech API): record with MediaRecorder and transcribe
 * the whole clip via the server whisper endpoint on stop.
 */
export function useDictation({ language = 'it' } = {}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const onUpdateRef = useRef(null);
  const finalRef = useRef('');

  const supportsLive = Boolean(SpeechRecognition);

  const startLive = useCallback((onUpdate) => {
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = toBcp47(language);
    recognition.continuous = true;
    recognition.interimResults = true;
    finalRef.current = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        if (res.isFinal) finalRef.current += res[0].transcript;
        else interim += res[0].transcript;
      }
      const text = (finalRef.current + interim).trim();
      if (onUpdate) onUpdate(text);
    };
    recognition.onerror = (e) => {
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        setError(e.error === 'not-allowed' ? 'Permesso microfono negato' : `Errore riconoscimento: ${e.error}`);
      }
    };
    recognition.onend = () => { setRecording(false); };

    try {
      recognition.start();
      setRecording(true);
    } catch (err) {
      setError(err.message || 'Avvio dettatura fallito');
    }
  }, [language]);

  const startBatch = useCallback(async (onUpdate) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const candidates = ['audio/webm', 'audio/ogg', 'audio/mp4'];
      const mimeType = candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        if (!blob.size) { setBusy(false); return; }
        setBusy(true);
        try {
          const ext = (mimeType || 'audio/webm').includes('mp4') ? 'mp4' : (mimeType || '').includes('ogg') ? 'ogg' : 'webm';
          const fd = new FormData();
          fd.append('audio', blob, `dictation.${ext}`);
          fd.append('language', language);
          const res = await aiApi.transcribe(fd);
          if (res && res.text && onUpdate) onUpdate(res.text);
        } catch (err) {
          setError(err && err.message ? err.message : 'Trascrizione fallita');
        } finally {
          setBusy(false);
        }
      };
      recorder.start();
      setRecording(true);
    } catch (err) {
      setError(err && err.name === 'NotAllowedError' ? 'Permesso microfono negato' : (err.message || 'Microfono non disponibile'));
      setRecording(false);
    }
  }, [language]);

  const start = useCallback((onUpdate) => {
    setError(null);
    onUpdateRef.current = onUpdate;
    if (supportsLive) startLive(onUpdate);
    else startBatch(onUpdate);
  }, [supportsLive, startLive, startBatch]);

  const stop = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (_e) { /* */ }
      recognitionRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  return { recording, busy, error, supportsLive, start, stop };
}
